import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, Link } from 'react-router-dom'
import { superadminApi } from '@/api/superadmin'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Plus, Users, Sparkles, LogIn, Trash2, Ban, CheckCircle2, Upload } from 'lucide-react'
import { ActionsMenu } from '@/components/shared/ActionsMenu'
import CreateTenantModal from './CreateTenantModal'
import DeleteTenantModal from './DeleteTenantModal'
import ImportTenantModal from './ImportTenantModal'

function fmt(date: string) {
  return new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

export default function TenantsListPage() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { impersonate } = useAuth()
  const [showCreate, setShowCreate] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null)
  const [importTarget, setImportTarget] = useState<{ id: string; name: string } | null>(null)

  const { data: tenants = [], isLoading } = useQuery({
    queryKey: ['superadmin-tenants'],
    queryFn: () => superadminApi.getTenants().then(r => r.data),
  })

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      superadminApi.setTenantActive(id, isActive),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['superadmin-tenants'] }),
  })

  const [seedingId, setSeedingId] = useState<string | null>(null)
  const seedMutation = useMutation({
    mutationFn: ({ id, force }: { id: string; force: boolean }) =>
      superadminApi.seedDemoData(id, force),
    onSettled: () => setSeedingId(null),
  })

  const impersonateMutation = useMutation({
    mutationFn: (id: string) => superadminApi.impersonateTenant(id).then(r => r.data),
    onSuccess: (data) => {
      impersonate(data.token, data.tenantName)
      navigate('/admin/overview')
    },
  })

  function handleSeed(id: string, name: string) {
    if (!confirm(`Seed demo data into "${name}"?\n\nAdds 4 clients, 26 staff, 6 courses, 3 trainers, 9 bookings and 9 invoices.`))
      return
    setSeedingId(id)
    seedMutation.mutate({ id, force: false }, {
      onError: (err: any) => {
        if (err?.response?.status === 409) {
          if (confirm(`"${name}" already has demo data.\n\nWipe ALL tenant data and reseed from scratch?\n\nThis cannot be undone.`)) {
            setSeedingId(id)
            seedMutation.mutate({ id, force: true })
          }
        }
      },
    })
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tenants</h1>
          <p className="text-sm text-gray-500 mt-1">{tenants.length} tenant{tenants.length !== 1 ? 's' : ''} registered</p>
        </div>
        <Button onClick={() => setShowCreate(true)}>
          <Plus size={15} className="mr-1.5" /> New tenant
        </Button>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-x-auto">
        <table className="w-max min-w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Slug</th>
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Users</th>
              <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Created</th>
              <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr><td colSpan={6} className="text-center text-gray-400 py-12">Loading…</td></tr>
            )}
            {!isLoading && tenants.length === 0 && (
              <tr><td colSpan={7} className="text-center text-gray-400 py-12">No tenants yet.</td></tr>
            )}
            {tenants.map(t => (
              <tr key={t.id} className="hover:bg-gray-50/60">
                <td className="px-5 py-4 font-medium">
                  <Link to={`/superadmin/tenants/${t.id}`} className="text-indigo-600 hover:text-indigo-700 hover:underline">{t.name}</Link>
                </td>
                <td className="px-5 py-4 text-gray-500 font-mono text-xs">{t.slug}</td>
                <td className="px-5 py-4">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                    t.isActive
                      ? 'bg-green-50 text-green-700 border-green-200'
                      : 'bg-gray-100 text-gray-500 border-gray-200'
                  }`}>
                    {t.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-5 py-4 text-gray-600">
                  <span className="inline-flex items-center gap-1.5">
                    <Users size={13} className="text-gray-400" />
                    {t.userCount}
                  </span>
                </td>
                <td className="px-5 py-4 text-gray-500">{fmt(t.createdAt)}</td>
                <td className="px-5 py-4 text-right">
                  <ActionsMenu
                    items={[
                      {
                        label: seedingId === t.id ? 'Seeding…' : 'Seed demo data',
                        icon: <Sparkles size={14} className="text-amber-600" />,
                        onClick: () => handleSeed(t.id, t.name),
                        disabled: seedingId === t.id,
                      },
                      {
                        label: "Log in as this tenant's admin",
                        icon: <LogIn size={14} className="text-violet-600" />,
                        onClick: () => impersonateMutation.mutate(t.id),
                        disabled: impersonateMutation.isPending,
                      },
                      {
                        label: t.isActive ? 'Deactivate tenant' : 'Activate tenant',
                        icon: t.isActive
                          ? <Ban size={14} className="text-red-600" />
                          : <CheckCircle2 size={14} className="text-green-600" />,
                        onClick: () => toggleMutation.mutate({ id: t.id, isActive: !t.isActive }),
                        disabled: toggleMutation.isPending,
                      },
                      {
                        label: 'Import roster',
                        icon: <Upload size={14} className="text-blue-600" />,
                        onClick: () => setImportTarget({ id: t.id, name: t.name }),
                      },
                      {
                        label: 'Delete tenant',
                        icon: <Trash2 size={14} className="text-red-600" />,
                        onClick: () => setDeleteTarget({ id: t.id, name: t.name }),
                        danger: true,
                      },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateTenantModal onClose={() => setShowCreate(false)} />}
      {deleteTarget && <DeleteTenantModal tenant={deleteTarget} onClose={() => setDeleteTarget(null)} />}
      {importTarget && <ImportTenantModal tenant={importTarget} onClose={() => setImportTarget(null)} />}
    </div>
  )
}
